import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleDot, Square, History } from 'lucide-react';
import { fetchJson, authHeaders } from '@/services/api-client';
import { CLIENT_CONFIG } from '@/config';
import { useTimelineStore } from '@/stores/timeline-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** A mission session as returned by the API. */
interface MissionSession {
  id: string;
  name: string;
  joinCode: string;
  audioRef: string;
  startedAt: string;
  endedAt: string | null;
}

const SESSIONS_KEY = ['workspace', 'sessions'] as const;

/** POST a session action. */
async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(
    `${CLIENT_CONFIG.apiBase}${url}`,
    authHeaders({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
  if (!res.ok) throw new Error(`POST ${url} failed: ${res.status}`);
  return (await res.json()) as T;
}

/** Enter replay for a session: pin the timeline to its window and scrub to start. */
function replaySession(session: MissionSession): void {
  const start = new Date(session.startedAt).getTime();
  const end = session.endedAt ? new Date(session.endedAt).getTime() : Date.now();
  const timeline = useTimelineStore.getState();
  timeline.setRange(start, end);
  timeline.scrubTo(start);
}

/**
 * Mission session recorder + replay list. Starting a session marks a recording
 * window (movement is captured continuously server-side); Replay pins the
 * timeline scrubber to the session window — deep history streams from the
 * server. The audio reference plays alongside once mission audio recording is
 * attached to the session.
 */
export function MissionSessions(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const sessions = useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: () => fetchJson<MissionSession[]>('/api/sessions', []),
  });

  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
  const start = useMutation({
    mutationFn: () => post('/api/sessions', { name }),
    onSuccess: () => {
      setName('');
      void refresh();
    },
  });
  const end = useMutation({
    mutationFn: (id: string) => post(`/api/sessions/${id}/end`),
    onSuccess: refresh,
  });

  const active = (sessions.data ?? []).find((session) => session.endedAt === null) ?? null;

  return (
    <div className="space-y-3">
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Mission Sessions
      </h3>

      {active ? (
        <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2">
          <CircleDot className="h-3.5 w-3.5 animate-pulse text-red-400" />
          <span className="flex-1 truncate text-xs text-foreground">Recording: {active.name}</span>
          <Button size="sm" variant="ghost" className="gap-1 text-xs" onClick={() => end.mutate(active.id)}>
            <Square className="h-3 w-3" /> End
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={name}
            placeholder="Session name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) start.mutate();
            }}
          />
          <Button size="sm" disabled={!name.trim() || start.isPending} onClick={() => start.mutate()}>
            Record
          </Button>
        </div>
      )}

      <ul className="space-y-1">
        {(sessions.data ?? [])
          .filter((session) => session.endedAt !== null)
          .map((session) => (
            <li
              key={session.id}
              className="flex items-center gap-2 rounded-md bg-white/[0.04] px-3 py-2"
            >
              <span className="flex-1 truncate text-xs text-foreground">{session.name}</span>
              <span className="text-[10px] text-muted-foreground">
                {new Date(session.startedAt).toLocaleString()}
              </span>
              <button
                type="button"
                title="Replay this session"
                onClick={() => replaySession(session)}
                className="flex items-center gap-1 rounded bg-white/[0.08] px-2 py-1 text-[11px] text-foreground hover:bg-white/[0.12]"
              >
                <History className="h-3 w-3" /> Replay
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
}
